import React, { Fragment, useRef } from 'react';

import Link from '@soluto-design/link';
import { Button, Icon, Text, useIsBreakpointUp } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import Media, { MediaProps } from '../../molecules/Media';

import { useHover } from '../../../hooks/useHover';

export interface PersonProps {
    name: string;
    image: MediaProps['image'];
    roleLabel?: string;
    href?: string;
    role?: string;
    previewImage?: MediaProps['image'];
    buttonLabel?: string;
    social?: {
        label: string;
        href: string;
    }[];
    bio?: string;
    disablePopover?: boolean;
}

function PersonPart({ image, name }: PersonProps) {
    return (
        <div className="flex gap-8 min-h-24">
            {image && (
                <div className="w-24 !h-24 rounded-full overflow-hidden">
                    <Media
                        media={image}
                        layout="responsive"
                        className="h-full"
                    />
                </div>
            )}
            <Text
                component="span"
                className="text-c100 text-text-secondary self-center">
                {name}
            </Text>
        </div>
    );
}

function Person({
    image,
    name,
    roleLabel,
    role,
    href,
    previewImage,
    buttonLabel,
    social,
    bio,
    disablePopover = false,
}: PersonProps) {
    const ref = useRef(null);

    const isHovered = useHover(ref);

    const isMD = useIsBreakpointUp('md');

    return (
        <div>
            <div className="flex gap-8" ref={ref}>
                {roleLabel && (
                    <Text
                        component="span"
                        className="text-c100 text-text-primary self-center">
                        {roleLabel}
                    </Text>
                )}
                <span ref={ref} className="relative flex items-center">
                    {href ? (
                        <Link
                            href={href}
                            alt={name}
                            className="font-regular !no-underline flex gap-8">
                            <PersonPart image={image} name={name} />
                        </Link>
                    ) : (
                        <PersonPart image={image} name={name} />
                    )}
                    {!disablePopover && isMD && (
                        <div
                            className={cls(
                                'absolute top-full pointer-events-none z-50 left-0',
                                isHovered && 'pointer-events-auto',
                            )}>
                            <div
                                className={cls(
                                    'rounded-large overflow-hidden bg-background-brand-primary flex min-h-[125px] min-w-[320px] md:min-w-[512px] xl:min-w-[580px] pointer-events-none transition-opacity duration-300 ease-in-out opacity-0 mt-16',
                                    isHovered &&
                                        'visible pointer-events-auto opacity-100',
                                )}>
                                {previewImage && (
                                    <div className="basis-4/12">
                                        <Media
                                            media={previewImage}
                                            layout="responsive"
                                            className="h-full"
                                        />
                                    </div>
                                )}
                                <div className={cls('flex-1 p-24')}>
                                    <Text
                                        component="p"
                                        className="text-h400 text-text-primary">
                                        {name}
                                    </Text>
                                    {role && (
                                        <Text
                                            component="p"
                                            className="text-c100 text-text-primary mt-sm">
                                            {role}
                                        </Text>
                                    )}
                                    {bio && (
                                        <Text
                                            component="div"
                                            isHtml
                                            className="text-c100 text-text-primary mt-sm line-clamp-3">
                                            {bio}
                                        </Text>
                                    )}
                                    {(href || social) && (
                                        <div className="flex gap-16 flex-col md:flex-row mt-32 md:items-center">
                                            {href && (
                                                <Button
                                                    href={href}
                                                    variant="tertiary"
                                                    size="medium"
                                                    rightIcon="arrow-direction--type-right">
                                                    {buttonLabel ?? 'Read more'}
                                                </Button>
                                            )}
                                            {social && social?.length > 0 && (
                                                <div className="flex gap-16 items-center">
                                                    {social.map((item, i) => {
                                                        return (
                                                            <Button
                                                                key={`social-${item?.href}-${item?.label}`}
                                                                href={
                                                                    item?.href
                                                                }
                                                                size="icon"
                                                                variant="tertiary"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                leftIcon={
                                                                    item?.label
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </span>
            </div>
        </div>
    );
}

export interface TagAuthorProps {
    persons?: PersonProps[];
    className?: string;
    disablePopover?: boolean;
}

export function TagAuthor({
    persons = [],
    className,
    disablePopover,
}: TagAuthorProps) {
    return (
        <div
            className={cls(
                'w-fit flex gap-x-4 gap-y-xxs flex-wrap',
                className,
            )}>
            {persons.map((person, i) => (
                <Fragment key={`author-tag-${i}`}>
                    <Person
                        {...person}
                        disablePopover={disablePopover || !person.href}
                    />
                    {i < persons.length - 1 && (
                        <Icon
                            icon="divider"
                            className="self-center !h-24 !w-24"
                        />
                    )}
                </Fragment>
            ))}
        </div>
    );
}

export default TagAuthor;
